import { ButtonHTMLAttributes, forwardRef } from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from '@flowstate/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        /** UX: Min 44×44px touch target (WCAG / mobile). */
        icon: "min-h-[44px] min-w-[44px] h-10 w-10",
      },
      loading: {
        true: "opacity-70 cursor-not-allowed",
        false: "",
      },
    },
    compoundVariants: [
      // Loading state variants
      {
        variant: "default",
        loading: true,
        class: "bg-primary/70 hover:bg-primary/70",
      },
      {
        variant: "destructive", 
        loading: true,
        class: "bg-destructive/70 hover:bg-destructive/70",
      },
      {
        variant: "outline",
        loading: true,
        class: "border-input/50 bg-accent/30",
      },
      {
        variant: "secondary",
        loading: true,
        class: "bg-secondary/60 hover:bg-secondary/60",
      },
      // Size + variant: keep min 44px for icon (touch target)
      {
        variant: "ghost",
        size: "icon",
        class: "min-h-[44px] min-w-[44px] p-0",
      },
      {
        variant: "outline",
        size: "icon",
        class: "min-h-[44px] min-w-[44px] p-0 border-2",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      loading: false,
    },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading = false, asChild = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // When using asChild, we can't render the loading spinner as a separate child
    // because Slot expects exactly one child. The loading state should be handled
    // by the parent component when using asChild.
    const buttonContent = asChild ? children : (
      <>
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </>
    )
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, loading, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {buttonContent}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
